import React, { useEffect, useState } from 'react'
import { Button, Checkbox, Dialog, FormControlLabel } from '@material-ui/core'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { defaultKeeps, UnverifiedSource } from 'app/lib/funnel-template'

export interface ApplyTemplateDialogProps {
  open: boolean
  templateName: string
  unverified: UnverifiedSource[]
  /** False on the create page, where no business-specific source can be checked yet. */
  businessKnown: boolean
  onCancel: () => void
  /** Called with the replacement keys and the kept source keys the user left checked. */
  onApply: (acceptedReplacements: string[], acceptedKeeps: string[]) => void
}

const replacementKeys = (unverified: UnverifiedSource[]) =>
  unverified.flatMap(item => (item.replacement ? [item.replacement.key] : []))

/**
 * Shown before a template is applied whenever it has a per-business source this
 * business can't confirm, so the user decides what to keep, replace or remove.
 */
export function ApplyTemplateDialog({
  open,
  templateName,
  unverified,
  businessKnown,
  onCancel,
  onApply,
}: ApplyTemplateDialogProps) {
  // Same-named replacements start checked: an exact name match on this
  // business is almost always the conversion the template meant.
  const [accepted, setAccepted] = useState<Set<string>>(new Set(replacementKeys(unverified)))
  const [keeps, setKeeps] = useState<Set<string>>(new Set(defaultKeeps(unverified, businessKnown)))
  useEffect(() => {
    setAccepted(new Set(replacementKeys(unverified)))
    setKeeps(new Set(defaultKeeps(unverified, businessKnown)))
  }, [unverified, businessKnown])

  const without = (set: Set<string>, key: string) => {
    const next = new Set(set)
    next.delete(key)
    return next
  }
  const toggle = (set: Set<string>, key: string) =>
    set.has(key) ? without(set, key) : new Set(set).add(key)

  // Replacing and keeping are exclusive: checking one unchecks the other.
  const toggleReplacement = (item: UnverifiedSource) => {
    setAccepted(current => toggle(current, item.replacement!.key))
    setKeeps(current => without(current, item.key))
  }
  const toggleKeep = (item: UnverifiedSource) => {
    setKeeps(current => toggle(current, item.key))
    if (item.replacement) setAccepted(current => without(current, item.replacement!.key))
  }

  const stages = Array.from(new Set(unverified.map(item => item.stageIndex))).map(stageIndex =>
    unverified.filter(item => item.stageIndex === stageIndex)
  )

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onCancel}>
      <DialogTitle>Apply “{templateName}”?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {businessKnown
            ? 'Some data sources in this template are specific to the business it was saved from, and this business doesn’t have them.'
            : 'This business hasn’t been created yet, so its business-specific data sources can’t be checked.'}{' '}
          Sources not kept or replaced will be removed. You can pick replacements in the editor.
        </DialogContentText>
        {stages.map(items => (
          <div key={items[0].stageIndex} className="mb-3" data-testid="unverified-stage">
            <h6 className="mb-2">{items[0].stageName}</h6>
            <ul className="list-unstyled pl-3 mb-0">
              {items.map(item => (
                <li key={`${item.groupIndex}-${item.key}`} className="mb-2">
                  <div>
                    <span className="text-muted">{item.groupName} → </span>
                    <strong>{item.name}</strong>
                    {item.name !== item.key && (
                      <span className="text-muted small ml-1">({item.key})</span>
                    )}
                  </div>
                  {item.kind === 'custom_conversion' ? (
                    <div className="text-danger small">
                      {businessKnown
                        ? 'Custom conversion. It belongs to another ad account and won’t report anything in this business.'
                        : 'Custom conversion. It only reports if this business uses the ad account the template was saved from.'}
                    </div>
                  ) : (
                    <div className="text-warning small">
                      Insight. It may have no data here if this ad account hasn’t recorded it.
                    </div>
                  )}
                  {item.replacement && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={accepted.has(item.replacement.key)}
                          onChange={() => toggleReplacement(item)}
                        />
                      }
                      label={`Replace with this business’s “${item.replacement.name}”`}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={keeps.has(item.key)}
                        onChange={() => toggleKeep(item)}
                      />
                    }
                    label="Keep anyway"
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button color="primary" onClick={() => onApply(Array.from(accepted), Array.from(keeps))}>
          Apply anyway
        </Button>
      </DialogActions>
    </Dialog>
  )
}
